import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  ErrorHandler,
  inject,
  Service,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SwUpdate,
  VersionEvent,
} from '@angular/service-worker';
import {
  catchError,
  EMPTY,
  exhaustMap,
  from,
  fromEvent,
  timer,
} from 'rxjs';
import { environment } from '../../environments/environment';

const UPDATE_ATTEMPT_PREFIX = 'atlas-app:update-attempt:';
const UNRECOVERABLE_ATTEMPT_KEY = `${UPDATE_ATTEMPT_PREFIX}unrecoverable`;

/**
 * Keeps the running application current without interrupting active work.
 *
 * A ready update is activated when the tab becomes hidden, then the page reloads so the old and
 * new application bundles are never mixed. Each version is attempted once per tab to prevent a
 * failed activation from causing a refresh loop.
 */
@Service()
export class AppUpdateService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly memoryAttemptKeys = new Set<string>();

  private activationInProgress = false;
  private pendingVersionHash?: string;

  constructor() {
    const swUpdate = this.swUpdate;
    if (!swUpdate?.isEnabled) {
      return;
    }

    swUpdate.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.handleVersionEvent(event));

    swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.reportError('The service worker entered an unrecoverable state.', event.reason);
      this.reloadOnce(UNRECOVERABLE_ATTEMPT_KEY);
    });

    fromEvent(this.document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyPendingUpdateWhenHidden());

    timer(0, environment.UPDATE_CHECK_INTERVAL_MS)
      .pipe(
        exhaustMap(() =>
          from(swUpdate.checkForUpdate()).pipe(
            catchError((error) => {
              this.reportError('The service worker could not check for an update.', error);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleVersionEvent(event: VersionEvent): void {
    if (event.type === 'VERSION_READY') {
      this.pendingVersionHash = event.latestVersion.hash;
      this.applyPendingUpdateWhenHidden();
      return;
    }

    if (event.type === 'VERSION_INSTALLATION_FAILED') {
      this.reportError(
        `Service-worker version ${event.version.hash} failed to install.`,
        event.error,
      );
    }
  }

  private applyPendingUpdateWhenHidden(): void {
    const versionHash = this.pendingVersionHash;
    if (this.document.visibilityState !== 'hidden' || !versionHash || this.activationInProgress) {
      return;
    }

    this.pendingVersionHash = undefined;
    if (!this.claimAttempt(`${UPDATE_ATTEMPT_PREFIX}${versionHash}`)) {
      return;
    }

    this.activationInProgress = true;
    void this.activateAndReload(versionHash);
  }

  private async activateAndReload(versionHash: string): Promise<void> {
    try {
      await this.swUpdate?.activateUpdate();
      this.document.defaultView?.location.reload();
    } catch (error) {
      this.reportError(`Service-worker version ${versionHash} could not be activated.`, error);
    } finally {
      this.activationInProgress = false;
    }
  }

  private reloadOnce(attemptKey: string): void {
    if (this.claimAttempt(attemptKey)) {
      this.document.defaultView?.location.reload();
    }
  }

  private claimAttempt(key: string): boolean {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (storage) {
        if (storage.getItem(key)) {
          return false;
        }

        storage.setItem(key, '1');
        return true;
      }
    } catch {
      // Some browser privacy modes block session storage; use a tab-local memory guard instead.
    }

    if (this.memoryAttemptKeys.has(key)) {
      return false;
    }

    this.memoryAttemptKeys.add(key);
    return true;
  }

  private reportError(message: string, cause: unknown): void {
    this.errorHandler.handleError(new Error(message, { cause }));
  }
}
